import { useMemo, useState } from 'react';

import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  CONTENT_LABELS,
  CONTENT_ORDER,
  type BgtHistoryDay,
  type ContentType,
} from '@/lib/api/bgt-history';

interface Props {
  days: BgtHistoryDay[];
}

interface SeriesDef {
  key: ContentType | 'avgScore';
  label: string;
  color: string;
}

/** 의뢰서 5종 + 평균 — 색상은 의뢰서 5-1 데이터 표시 원칙과 충돌 없는 컬러풀 팔레트. */
const SERIES: SeriesDef[] = [
  { key: 'avgScore', label: '평균', color: '#2C5282' },
  { key: 'MAKE_SENTENCE', label: CONTENT_LABELS.MAKE_SENTENCE, color: '#1976D2' },
  { key: 'ANSWER_SENTENCE', label: CONTENT_LABELS.ANSWER_SENTENCE, color: '#9C27B0' },
  { key: 'WORD', label: CONTENT_LABELS.WORD, color: '#2E7D32' },
  { key: 'ORDER_WORD', label: CONTENT_LABELS.ORDER_WORD, color: '#ED6C02' },
  { key: 'PHONEME', label: CONTENT_LABELS.PHONEME, color: '#D81B60' },
];

/**
 * 의뢰서 5-2 탭2 콘텐츠별 꺾은선 그래프 (P5-3).
 *
 * - 5종 콘텐츠 + 평균선 = 6 시리즈
 * - Legend 클릭으로 시리즈 토글
 * - 사용 안 한 날 값은 null → 자연스러운 단절(connectNulls=false)
 * - Tooltip에 호버 일자의 모든 활성 시리즈 표시
 */
export function BgtHistoryChart({ days }: Props) {
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const data = useMemo(
    () =>
      days.map((d) => {
        const row: Record<string, number | string | null> = { date: d.date };
        row.avgScore = d.hasData ? d.avgScore : null;
        for (const t of CONTENT_ORDER) {
          row[t] = d.contentScores[t] ?? null;
        }
        return row;
      }),
    [days],
  );

  const hasAnyActive = days.some((d) => d.hasData);

  if (!hasAnyActive) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            콘텐츠별 추이
          </Typography>
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.disabled">
              선택한 구간에 BGT 사용 기록이 없습니다.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const toggleSeries = (key: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            콘텐츠별 추이
          </Typography>
          <Typography variant="caption" color="text.disabled">
            범례 클릭으로 토글 · 빈 칸은 미사용일
          </Typography>
        </Stack>
        <Box sx={{ width: '100%', height: 340 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value, name) => {
                  const v = value as number | null | undefined;
                  return [v === null || v === undefined ? '—' : `${v}점`, name as string];
                }}
              />
              <Legend
                onClick={(e) => toggleSeries((e as { dataKey?: string }).dataKey ?? '')}
                wrapperStyle={{ cursor: 'pointer', fontSize: 12 }}
              />
              {SERIES.map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  strokeWidth={s.key === 'avgScore' ? 2.5 : 1.5}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  hide={hidden.has(s.key)}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
}
